import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, Delete, Download, Upload, Edit } from '@mui/icons-material';
import {
  downloadLocalFile,
  getReceivingDocumentTypeLabel,
  RECEIVING_LINE_DOCUMENT_TYPES
} from '../../utils/storeCertificateDocuments';
import {
  RECEIVING_PRIMARY_CONDITIONS,
  LEGACY_PART_CONDITIONS,
  PART_CONDITION_LABELS,
  partConditionSelectValue,
  deriveReceivingLineCondition,
  receivingUnitDetailCount
} from '../../constants/partUnitConditions';
import { STORE_TYPES, normalizeStoreTypeValue } from '../../constants/storeTypes';
import {
  formatPartMasterOptionLabel,
  inventoryOptionLabel,
  inventoryPickerOptions,
  inventorySerialOptions
} from '../../utils/partMasterLabel';
import { PO_LINE_CLOSURE_REASON_LABELS } from '../../constants/purchaseOrderLineClosure';
import { unitMetricLabel, unitSelectOptions } from '../../constants/unitMetrics';
import {
  INVENTORY_CATEGORIES,
  INVENTORY_CATEGORY_LABELS,
  normalizeInventoryCategory
} from '../../constants/inventoryCategories';
import InventoryItemFormDialog from './InventoryItemFormDialog';
import { useToast } from '../../contexts/ToastContext';
import {
  formatLineSerialNumber,
  formatReceivingLocation,
  hasReceivingLocation,
  isShelfLifeNaValue,
  normalizeReceivingLocation,
  normalizeUnitExpiryForForm,
  SHELF_LIFE_NA_VALUE,
  toDateInputValue as toDateInputValueShared,
  unitExpiryForPersistence,
  uniqueNonEmptyStrings,
  withLineStoreTypeOnUnits
} from '../../utils/inventoryItemForm';

const RECEIVING_CONDITION_SELECT_VALUES = [...RECEIVING_PRIMARY_CONDITIONS, ...LEGACY_PART_CONDITIONS];

/** Normalize a stored date (ISO string / Date) to a `YYYY-MM-DD` value for date inputs. */
const toDateInputValue = toDateInputValueShared;

export const createEmptyReceivingItem = () => ({
  partNumber: '',
  referencePartNumber: '',
  description: '',
  quantity: 0,
  receivedQuantity: 0,
  unit: 'EACH',
  condition: 'SERVICEABLE',
  category: 'PART',
  certificateNumber: '',
  batchNumber: '',
  serialNumber: '',
  unitDetails: [],
  storeType: '',
  storePartCode: '',
  locationText: '',
  location: { warehouse: '', shelf: '', bin: '' },
  reorderPoint: 0,
  minimumStock: 0,
  remarks: '',
  shelfLifeUpto: '',
  shelfLifeNotApplicable: false,
  inventoryItemId: '',
  partSearch: '',
  serialNumbers: []
});

const ReceivingItemsGrid = ({
  items,
  onItemsChange,
  selectedPoId = '',
  certificateFilesByRow = {},
  certificateServerDocsByRow = {},
  onCertificateServerDownload,
  downloadingCertificateRowKey = '',
  onCertificateFileChange,
  onCertificatePendingRemove,
  inventoryOptions = null,
  issueMode = false,
  showCertificateUpload = true,
  title = 'Items',
  emptyMessage = 'Add items to receive',
  poEmptyMessage = 'Select a purchase order to load its lines.',
  onRemoveItem,
  aircraftList = [],
  headerAircraft = null
}) => {
  const { showToast } = useToast();
  const inventoryById = useMemo(
    () => new Map((Array.isArray(inventoryOptions) ? inventoryOptions : []).map((row) => [String(row._id), row])),
    [inventoryOptions]
  );
  const useInventoryPicker = issueMode && Array.isArray(inventoryOptions);

  const [unitDetailsDialog, setUnitDetailsDialog] = useState({
    open: false,
    rowIndex: null,
    partNumber: '',
    shelfLifeNotApplicable: false,
    rows: []
  });
  const [docTypeByRow, setDocTypeByRow] = useState({});
  const [lineFormDialog, setLineFormDialog] = useState({
    open: false,
    rowIndex: null
  });

  const handleAddItem = () => {
    onItemsChange([...items, createEmptyReceivingItem()]);
  };

  const handleRemoveItem = (index) => {
    if (onRemoveItem) {
      onRemoveItem(index);
      return;
    }
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const isPoLineClosed = (item) => selectedPoId && item?.lineStatus === 'CLOSED';

  const handleItemChange = (index, field, value) => {
    if (field === 'receivedQuantity' && isPoLineClosed(items[index])) return;
    const newItems = items.map((row, i) => {
      if (i !== index) return row;
      const next = { ...row, [field]: value };
      const lineCondition = partConditionSelectValue(next.condition);
      if (field === 'receivedQuantity') {
        const nextQty = Math.max(0, parseFloat(value) || 0);
        const unitCount = receivingUnitDetailCount(nextQty);
        const currentRows = Array.isArray(next.unitDetails) ? next.unitDetails : [];
        const lineStore = normalizeStoreTypeValue(next.storeType) || '';
        next.unitDetails = Array.from({ length: unitCount }, (_, unitIndex) => (
          currentRows[unitIndex] || {
            condition: lineCondition,
            batchNumber: '',
            serialNumber: '',
            storeType: lineStore
          }
        ));
        if (issueMode && Array.isArray(next.serialNumbers) && next.serialNumbers.length > nextQty) {
          next.serialNumbers = next.serialNumbers.slice(0, nextQty);
        }
      }
      if (field === 'storeType') {
        next.unitDetails = withLineStoreTypeOnUnits(next.unitDetails, value, { overwrite: true });
      }
      if (field === 'condition') {
        const condition = partConditionSelectValue(value);
        next.condition = condition;
        if (Array.isArray(next.unitDetails) && next.unitDetails.length > 0) {
          next.unitDetails = next.unitDetails.map((unit) => ({ ...unit, condition }));
        }
      }
      if (field === 'serialNumbers') {
        const serials = Array.from(
          new Set((Array.isArray(value) ? value : []).map((s) => String(s || '').trim()).filter(Boolean))
        );
        const maxQty = Math.max(serials.length, Math.floor(Number(next.receivedQuantity) || 0));
        next.serialNumbers = serials.slice(0, maxQty || serials.length);
        next.unitDetails = next.serialNumbers.map((serial, unitIndex) => ({
          ...(Array.isArray(next.unitDetails) ? next.unitDetails[unitIndex] : null) || {
            condition: lineCondition,
            batchNumber: '',
            storeType: normalizeStoreTypeValue(next.storeType) || ''
          },
          serialNumber: serial
        }));
        next.receivedQuantity = Math.max(Number(next.receivedQuantity) || 0, next.serialNumbers.length) || next.receivedQuantity;
      }
      return next;
    });
    onItemsChange(newItems);
  };

  const handleInventorySelect = (index, option) => {
    onItemsChange(items.map((row, i) => {
      if (i !== index) return row;
      if (!option) {
      return {
        ...row,
        inventoryItemId: '',
        partNumber: '',
        referencePartNumber: '',
        description: '',
        partSearch: '',
        serialNumbers: [],
        storeType: row.storeType || '',
        locationText: row.locationText || '',
        location: normalizeReceivingLocation(row.location)
      };
      }
      return {
        ...row,
        inventoryItemId: option._id || '',
        partNumber: option.partNumber || '',
        referencePartNumber: option.referencePartNumber || '',
        description: option.description || '',
        category: normalizeInventoryCategory(option.category || row.category),
        partSearch: option.partNumber || '',
        serialNumbers: [],
        storeType: option.storeType || row.storeType || '',
        locationText: option.locationText || row.locationText || '',
        location: hasReceivingLocation(option.location)
          ? normalizeReceivingLocation(option.location)
          : normalizeReceivingLocation(row.location)
      };
    }));
  };

  const openUnitDetailsDialog = (index) => {
    const item = items[index];
    if (!item) return;
    const qtyField = issueMode ? 'receivedQuantity' : 'receivedQuantity';
    const count = Math.max(0, Math.floor(Number(item[qtyField]) || 0));
    const existingRows = Array.isArray(item.unitDetails) ? item.unitDetails : [];
    const shelfLifeNotApplicable = Boolean(item.shelfLifeNotApplicable);
    const lineExpiry = normalizeUnitExpiryForForm(item.shelfLifeUpto, shelfLifeNotApplicable);
    const rows = Array.from({ length: count }, (_, unitIndex) => {
      const existing = existingRows[unitIndex];
      if (existing) {
        return {
          ...existing,
          storeType: normalizeStoreTypeValue(existing.storeType) || normalizeStoreTypeValue(item.storeType) || '',
          expiryDate: normalizeUnitExpiryForForm(existing.expiryDate, shelfLifeNotApplicable)
        };
      }
      return {
        condition: partConditionSelectValue(item.condition),
        batchNumber: '',
        serialNumber: '',
        storeType: normalizeStoreTypeValue(item.storeType) || '',
        expiryDate: lineExpiry
      };
    });
    setUnitDetailsDialog({
      open: true,
      rowIndex: index,
      partNumber: item.partNumber || '',
      shelfLifeNotApplicable,
      rows
    });
  };

  const closeUnitDetailsDialog = () => {
    setUnitDetailsDialog({
      open: false,
      rowIndex: null,
      partNumber: '',
      shelfLifeNotApplicable: false,
      rows: []
    });
  };

  const handleUnitDetailsChange = (unitIndex, field, value) => {
    setUnitDetailsDialog((prev) => {
      // Batch / serial edits cascade only to this row and matching rows below it.
      if (field === 'batchNumber' || field === 'serialNumber') {
        const previousValue = prev.rows[unitIndex]?.[field] || '';
        return {
          ...prev,
          rows: prev.rows.map((row, i) => {
            if (i < unitIndex) return row;
            if (i === unitIndex) return { ...(row || {}), [field]: value };
            return (row?.[field] || '') === previousValue
              ? { ...(row || {}), [field]: value }
              : row;
          })
        };
      }
      const nextRows = [...prev.rows];
      nextRows[unitIndex] = { ...(nextRows[unitIndex] || {}), [field]: value };
      return { ...prev, rows: nextRows };
    });
  };

  const saveUnitDetailsDialog = () => {
    const rowIndex = unitDetailsDialog.rowIndex;
    if (rowIndex == null) return;
    const serialNumbers = uniqueNonEmptyStrings(
      unitDetailsDialog.rows.map((unit) => unit?.serialNumber)
    );
    const unitDetails = unitDetailsDialog.rows.map((unit) => ({
      ...unit,
      expiryDate: isShelfLifeNaValue(unit?.expiryDate)
        ? SHELF_LIFE_NA_VALUE
        : (unitExpiryForPersistence(unit?.expiryDate) || '')
    }));
    const hasDatedExpiry = unitDetails.some((unit) => Boolean(unitExpiryForPersistence(unit?.expiryDate)));
    const derivedCondition = deriveReceivingLineCondition({
      condition: items[rowIndex]?.condition,
      unitDetails
    });
    onItemsChange(items.map((item, index) => (
      index === rowIndex
        ? {
          ...item,
          condition: derivedCondition,
          unitDetails,
          shelfLifeNotApplicable: hasDatedExpiry ? false : item.shelfLifeNotApplicable,
          serialNumbers,
          serialNumber: serialNumbers.length > 0
            ? formatLineSerialNumber(serialNumbers)
            : item.serialNumber
        }
        : item
    )));
    closeUnitDetailsDialog();
  };

  const openLineFormDialog = (index) => {
    setLineFormDialog({ open: true, rowIndex: index });
  };

  const closeLineFormDialog = () => {
    setLineFormDialog({ open: false, rowIndex: null });
  };

  const handleLineFormReturn = (mergedLine, errorMessage) => {
    if (errorMessage) {
      showToast(errorMessage, 'error');
      return;
    }
    const rowIndex = lineFormDialog.rowIndex;
    if (rowIndex == null || !mergedLine) return;
    onItemsChange(items.map((item, index) => (index === rowIndex ? mergedLine : item)));
    closeLineFormDialog();
  };

  const qtyColumnLabel = selectedPoId ? 'Ordered Qty' : (issueMode ? null : 'Quantity');
  const receivedQtyLabel = selectedPoId ? 'Receiving Now' : (issueMode ? 'Issue Qty' : 'Received Qty');

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {!selectedPoId && (
          <Button variant="outlined" startIcon={<Add />} onClick={handleAddItem}>
            Add Item
          </Button>
        )}
        {selectedPoId && (
          <Typography variant="body2" color="text.secondary">
            Lines from PO — enter received qty and details per line. Receive in multiple batches until fully received.
          </Typography>
        )}
      </Box>

      {items.length === 0 ? (
        <Alert severity="info">
          {selectedPoId ? poEmptyMessage : emptyMessage}
        </Alert>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 180, whiteSpace: 'nowrap' }}>Part Number</TableCell>
                <TableCell sx={{ minWidth: 160, whiteSpace: 'nowrap' }}>Reference Part Number</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                {useInventoryPicker && <TableCell>Available</TableCell>}
                {qtyColumnLabel && <TableCell>{qtyColumnLabel}</TableCell>}
                {selectedPoId && <TableCell>Already Received</TableCell>}
                {selectedPoId && <TableCell>Status</TableCell>}
                <TableCell>{receivedQtyLabel}</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Category</TableCell>
                {!selectedPoId && <TableCell>Condition</TableCell>}
                <TableCell>Store Type</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Location</TableCell>
                {useInventoryPicker && <TableCell sx={{ minWidth: 200 }}>Store Serials</TableCell>}
                {!selectedPoId && <TableCell sx={{ minWidth: 260 }}>Certificate Number</TableCell>}
                {!selectedPoId && <TableCell>Batch / Serial</TableCell>}
                {!selectedPoId && <TableCell>Shelf Life Upto</TableCell>}
                {!selectedPoId && <TableCell sx={{ minWidth: 260 }}>Remarks</TableCell>}
                {showCertificateUpload && <TableCell sx={{ minWidth: 280 }}>Documents (CoC / Log Card)</TableCell>}
                <TableCell>Item Details</TableCell>
                {!selectedPoId && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const selectedInventory = inventoryById.get(String(item.inventoryItemId));
                const pickerOptions = useInventoryPicker
                  ? inventoryPickerOptions(inventoryOptions, item.partSearch || '')
                  : [];
                const lineClosed = isPoLineClosed(item);
                return (
                  <TableRow key={index} sx={lineClosed ? { bgcolor: 'action.hover' } : undefined}>
                    <TableCell sx={{ minWidth: 180, verticalAlign: 'top' }}>
                      {selectedPoId ? (
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', py: 1 }}
                        >
                          {item.partNumber || '—'}
                        </Typography>
                      ) : useInventoryPicker ? (
                        <Autocomplete
                          size="small"
                          options={pickerOptions}
                          value={selectedInventory || null}
                          inputValue={item.partSearch || ''}
                          onInputChange={(_, value, reason) => {
                            if (reason === 'clear') {
                              onItemsChange(items.map((row, i) => (i === index ? {
                                ...row,
                                partSearch: '',
                                inventoryItemId: '',
                                partNumber: '',
                                description: '',
                                serialNumbers: []
                              } : row)));
                              return;
                            }
                            // Ignore Autocomplete "reset" so inventory labels never overwrite search text.
                            if (reason !== 'input') return;
                            onItemsChange(items.map((row, i) => {
                              if (i !== index) return row;
                              const next = { ...row, partSearch: value };
                              if (selectedInventory && value !== String(selectedInventory.partNumber || '')) {
                                next.inventoryItemId = '';
                                next.partNumber = '';
                                next.description = '';
                                next.serialNumbers = [];
                              }
                              return next;
                            }));
                          }}
                          onChange={(_, option) => handleInventorySelect(index, option)}
                          filterOptions={(opts) => opts}
                          getOptionLabel={(option) => String(option?.partNumber || '')}
                          isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                          noOptionsText={
                            String(item.partSearch || '').trim()
                              ? 'No matching parts in store'
                              : 'Type part number to search store inventory'
                          }
                          renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                              {inventoryOptionLabel(option)}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              placeholder="Type part number to search"
                              sx={{ minWidth: 200 }}
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          required
                          value={item.partNumber}
                          onChange={(e) => handleItemChange(index, 'partNumber', e.target.value)}
                          sx={{ minWidth: 160 }}
                          inputProps={{ style: { whiteSpace: 'nowrap' } }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 160, verticalAlign: 'top' }}>
                      {selectedPoId ? (
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', py: 1 }}
                        >
                          {item.referencePartNumber || '—'}
                        </Typography>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          value={item.referencePartNumber || ''}
                          onChange={(e) => handleItemChange(index, 'referencePartNumber', e.target.value)}
                          sx={{ minWidth: 140 }}
                          inputProps={{ style: { whiteSpace: 'nowrap' } }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200, verticalAlign: 'top' }}>
                      <TextField
                        size="small"
                        fullWidth
                        required
                        label={selectedPoId ? undefined : 'Description'}
                        value={item.description || ''}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Description"
                        multiline={!selectedPoId && !useInventoryPicker}
                        minRows={selectedPoId || useInventoryPicker ? 1 : 2}
                        sx={{ minWidth: 180 }}
                        InputProps={{ readOnly: !!useInventoryPicker && !!selectedInventory }}
                      />
                    </TableCell>
                    {useInventoryPicker && (
                      <TableCell>
                        <Typography variant="body2">{selectedInventory?.quantity ?? 0}</Typography>
                      </TableCell>
                    )}
                    {qtyColumnLabel && (
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          InputProps={{ readOnly: !!selectedPoId }}
                          sx={selectedPoId ? { width: 100 } : {}}
                        />
                      </TableCell>
                    )}
                    {selectedPoId && (
                      <TableCell>
                        <Typography variant="body2">{item.alreadyReceived ?? 0}</Typography>
                      </TableCell>
                    )}
                    {selectedPoId && (
                      <TableCell>
                        {lineClosed ? (
                          <Tooltip title={item.closureRemarks || PO_LINE_CLOSURE_REASON_LABELS.PART_CANCELLED}>
                            <Chip
                              label={PO_LINE_CLOSURE_REASON_LABELS.PART_CANCELLED}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Chip label="Open" size="small" variant="outlined" />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{
                          min: 0,
                          step: 'any',
                          max: useInventoryPicker
                            ? (selectedInventory?.quantity || undefined)
                            : (selectedPoId && !lineClosed
                              ? Math.max(
                                0,
                                (Number(item.quantity) || 0) - (Number(item.alreadyReceived) || 0)
                              )
                              : undefined)
                        }}
                        value={lineClosed ? 0 : item.receivedQuantity}
                        onChange={(e) => handleItemChange(index, 'receivedQuantity', parseFloat(e.target.value) || 0)}
                        placeholder={selectedPoId ? '0' : ''}
                        InputProps={{ readOnly: lineClosed }}
                        sx={selectedPoId || issueMode ? { width: 110 } : {}}
                      />
                    </TableCell>
                    <TableCell>
                      {selectedPoId ? (
                        <Typography variant="body2" sx={{ minWidth: 110 }}>
                          {unitMetricLabel(item.unit || 'EACH')}
                        </Typography>
                      ) : (
                        <Select
                          size="small"
                          value={item.unit || 'EACH'}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          sx={{ minWidth: 110 }}
                        >
                          {unitSelectOptions(item.unit).map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unitMetricLabel(unit)}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={normalizeInventoryCategory(item.category)}
                        onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                        sx={{ minWidth: 130 }}
                        disabled={lineClosed}
                      >
                        {INVENTORY_CATEGORIES.map((category) => (
                          <MenuItem key={category} value={category}>
                            {INVENTORY_CATEGORY_LABELS[category]}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    {!selectedPoId && (
                      <TableCell>
                        <Select
                          size="small"
                          value={partConditionSelectValue(item.condition)}
                          onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                        >
                          {RECEIVING_CONDITION_SELECT_VALUES.map((v) => (
                            <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    )}
                    <TableCell>
                      <Select
                        size="small"
                        displayEmpty
                        value={normalizeStoreTypeValue(item.storeType)}
                        onChange={(e) => handleItemChange(index, 'storeType', e.target.value)}
                        sx={{ minWidth: 130 }}
                        disabled={lineClosed}
                        renderValue={(selected) => {
                          const opt = STORE_TYPES.find((t) => t.value === selected);
                          return opt ? opt.label : '\u00a0';
                        }}
                      >
                        <MenuItem value="">
                          <em>Select store</em>
                        </MenuItem>
                        {STORE_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell sx={{ minWidth: 220, verticalAlign: 'top' }}>
                      {hasReceivingLocation(item.location) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {formatReceivingLocation(item.location)}
                        </Typography>
                      )}
                      <TextField
                        fullWidth
                        size="small"
                        value={item.locationText || ''}
                        onChange={(e) => handleItemChange(index, 'locationText', e.target.value)}
                        placeholder="Location note"
                        disabled={lineClosed}
                        helperText={selectedPoId ? 'Warehouse / shelf / bin in Item Details' : undefined}
                      />
                    </TableCell>
                    {useInventoryPicker && (
                      <TableCell sx={{ minWidth: 200, verticalAlign: 'top' }}>
                        <Autocomplete
                          multiple
                          size="small"
                          disabled={!selectedInventory}
                          options={inventorySerialOptions(selectedInventory)}
                          value={Array.isArray(item.serialNumbers) ? item.serialNumbers : []}
                          onChange={(_, selected) => {
                            const maxQty = Math.floor(Number(item.receivedQuantity) || 0) || selected.length || 1;
                            const normalized = Array.from(
                              new Set((selected || []).map((serial) => String(serial || '').trim()).filter(Boolean))
                            ).slice(0, maxQty);
                            handleItemChange(index, 'serialNumbers', normalized);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                selectedInventory
                                  ? (inventorySerialOptions(selectedInventory).length
                                    ? 'Select serial(s) from store'
                                    : 'No serials on file for this part')
                                  : 'Select a part first'
                              }
                              helperText="Optional — choose serial numbers tracked in store inventory"
                            />
                          )}
                        />
                      </TableCell>
                    )}
                    {!selectedPoId && (
                      <TableCell sx={{ minWidth: 260, verticalAlign: 'top' }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={item.certificateNumber}
                          onChange={(e) => handleItemChange(index, 'certificateNumber', e.target.value)}
                          placeholder="Certificate Number"
                        />
                      </TableCell>
                    )}
                    {!selectedPoId && (
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.batchNumber}
                          onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                          placeholder="Batch"
                          sx={{ width: 90, mr: 0.5 }}
                        />
                        <TextField
                          size="small"
                          value={item.serialNumber}
                          onChange={(e) => handleItemChange(index, 'serialNumber', e.target.value)}
                          placeholder="Serial"
                          sx={{ width: 90 }}
                        />
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => openUnitDetailsDialog(index)}
                          sx={{ ml: 0.5 }}
                          disabled={(Number(item.receivedQuantity) || 0) <= 0}
                        >
                          Units ({Array.isArray(item.unitDetails) ? item.unitDetails.length : 0})
                        </Button>
                      </TableCell>
                    )}
                    {!selectedPoId && (
                      <TableCell>
                        {item.shelfLifeNotApplicable ? (
                          <Typography variant="body2" color="text.secondary">N/A</Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="date"
                            value={item.shelfLifeUpto || ''}
                            onChange={(e) => handleItemChange(index, 'shelfLifeUpto', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </TableCell>
                    )}
                    {!selectedPoId && (
                      <TableCell sx={{ minWidth: 260, verticalAlign: 'top' }}>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          minRows={2}
                          maxRows={4}
                          value={item.remarks || ''}
                          onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                          placeholder="Remarks"
                        />
                      </TableCell>
                    )}
                    {showCertificateUpload && (
                      <TableCell>
                        {(() => {
                          const pendingRaw = certificateFilesByRow[index];
                          const pendingFiles = Array.isArray(pendingRaw)
                            ? pendingRaw
                            : (pendingRaw ? [{ file: pendingRaw, docType: 'COC' }] : []);
                          const serverEntry = certificateServerDocsByRow[index];
                          const serverDocs = Array.isArray(serverEntry?.docs)
                            ? serverEntry.docs
                            : (serverEntry?.doc ? [serverEntry.doc] : []);
                          const selectedDocType = docTypeByRow[index] || 'COC';
                          return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 240 }}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Select
                                  size="small"
                                  value={selectedDocType}
                                  onChange={(e) => setDocTypeByRow((prev) => ({
                                    ...prev,
                                    [index]: e.target.value
                                  }))}
                                  sx={{ minWidth: 110 }}
                                >
                                  {RECEIVING_LINE_DOCUMENT_TYPES.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  component="label"
                                  startIcon={<Upload />}
                                >
                                  Add
                                  <input
                                    type="file"
                                    hidden
                                    multiple
                                    onChange={(e) => {
                                      onCertificateFileChange?.(index, e.target.files, selectedDocType);
                                      e.target.value = '';
                                    }}
                                  />
                                </Button>
                              </Box>
                              {pendingFiles.map((pending, pendingIndex) => {
                                const file = pending?.file || pending;
                                const typeLabel = RECEIVING_LINE_DOCUMENT_TYPES.find(
                                  (entry) => entry.value === (pending?.docType || 'COC')
                                )?.label || 'Document';
                                return (
                                  <Box
                                    key={`pending-${index}-${pendingIndex}-${file?.name || pendingIndex}`}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                  >
                                    <Chip label={typeLabel} size="small" />
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      title={file?.name}
                                      sx={{ flex: 1, minWidth: 0 }}
                                    >
                                      {file?.name || 'Selected file'}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => downloadLocalFile(file, file?.name)}
                                      aria-label="Download selected document"
                                    >
                                      <Download fontSize="inherit" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => onCertificatePendingRemove?.(index, pendingIndex)}
                                      aria-label="Remove selected document"
                                    >
                                      <Delete fontSize="inherit" />
                                    </IconButton>
                                  </Box>
                                );
                              })}
                              {serverDocs.map((doc) => (
                                <Box
                                  key={doc._id || doc.filename || doc.originalName}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                >
                                  <Chip
                                    label={getReceivingDocumentTypeLabel(doc.category)}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    title={doc.originalName || doc.filename}
                                    sx={{ flex: 1, minWidth: 0 }}
                                  >
                                    {doc.originalName || doc.filename || 'Document on file'}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    disabled={downloadingCertificateRowKey === `${index}:${doc._id}`}
                                    onClick={() => onCertificateServerDownload?.(index, {
                                      recordId: serverEntry.recordId,
                                      doc
                                    })}
                                    aria-label="Download saved document"
                                  >
                                    <Download fontSize="inherit" />
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          );
                        })()}
                      </TableCell>
                    )}
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() => openLineFormDialog(index)}
                      >
                        Details
                      </Button>
                    </TableCell>
                    {!selectedPoId && (
                      <TableCell>
                        <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={unitDetailsDialog.open}
        onClose={closeUnitDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Unit Batch / Serial Details {unitDetailsDialog.partNumber ? `- ${unitDetailsDialog.partNumber}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {unitDetailsDialog.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Set {issueMode ? 'issue' : 'received'} quantity greater than zero to enter per-unit details.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Unit #</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Batch Number (Optional)</TableCell>
                  <TableCell>Serial Number (Optional)</TableCell>
                  <TableCell>Shelf Life Upto (Optional)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unitDetailsDialog.rows.map((row, idx) => (
                  <TableRow key={`unit-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={partConditionSelectValue(row.condition)}
                        onChange={(e) => handleUnitDetailsChange(idx, 'condition', e.target.value)}
                      >
                        {RECEIVING_CONDITION_SELECT_VALUES.map((v) => (
                          <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.batchNumber || ''}
                        onChange={(e) => handleUnitDetailsChange(idx, 'batchNumber', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.serialNumber || ''}
                        onChange={(e) => handleUnitDetailsChange(idx, 'serialNumber', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      {isShelfLifeNaValue(row.expiryDate) ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={SHELF_LIFE_NA_VALUE}
                          onChange={(e) => {
                            const nextVal = e.target.value;
                            handleUnitDetailsChange(
                              idx,
                              'expiryDate',
                              isShelfLifeNaValue(nextVal)
                                ? SHELF_LIFE_NA_VALUE
                                : (toDateInputValue(nextVal) || nextVal || '')
                            );
                          }}
                          onFocus={(e) => e.target.select()}
                          placeholder="N/A or date"
                        />
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          type="date"
                          value={toDateInputValue(row.expiryDate)}
                          onChange={(e) => handleUnitDetailsChange(idx, 'expiryDate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUnitDetailsDialog}>Cancel</Button>
          <Button onClick={saveUnitDetailsDialog} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <InventoryItemFormDialog
        open={lineFormDialog.open}
        onClose={closeLineFormDialog}
        submitMode="return"
        initialLine={lineFormDialog.rowIndex != null ? items[lineFormDialog.rowIndex] : null}
        headerAircraft={headerAircraft}
        aircraftList={aircraftList}
        lockPartNumber={!!selectedPoId}
        readOnlyOrderedQty={
          lineFormDialog.rowIndex != null && selectedPoId
            ? items[lineFormDialog.rowIndex]?.quantity
            : null
        }
        showCertificateFields
        onReturn={handleLineFormReturn}
      />
    </Box>
  );
};

export default ReceivingItemsGrid;
